import { useEffect } from 'react'
import { Chip } from 'components/Chip/Chip'
import { CollabItemsFilterProps } from './Types'
import { useUserCard } from 'hooks/queries/users/useUserCard'
import {
  CollabItemsResolvedFilter,
  CollabItemsReviewFilter,
  CollabItemsUserFilter,
  CollabItemsFilter as CollabItemsFilterType,
  resolvedFilter,
} from 'stores/CollabItemsFilters'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useDocStore } from 'stores/DocStore'
import Avatar from 'common/Avatar/Avatar'
import { Check, Comment, Review, Task, TrackedChanges } from 'almanac-ui/icons'

interface FilterProps<T extends CollabItemsFilterType> {
  isSelected: boolean
  count: number
  filter: T
}

const CommentsFilter = ({ filter, count, ...props }: FilterProps<CollabItemsFilterType>) => (
  <Chip size="sm" icon={Comment} {...props}>
    Comments {count}
  </Chip>
)

const TrackedChangesFilter = ({ filter, count, ...props }: FilterProps<CollabItemsFilterType>) => {
  return (
    <Chip size="sm" icon={TrackedChanges} {...props}>
      Changes {count}
    </Chip>
  )
}

const TodosFilter = ({ filter, count, ...props }: FilterProps<CollabItemsFilterType>) => (
  <Chip size="sm" icon={Task} {...props}>
    Tasks {count}
  </Chip>
)

const UserFilter = ({ filter, count, ...props }: FilterProps<CollabItemsUserFilter>) => {
  const userQuery = useUserCard(filter.userId, {
    // 1 day
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnMount: false,
  })
  const user = userQuery.data?.user
  const name = user?.display_name || ''

  if (!user) {
    return null
  }

  return (
    <Chip size="sm" avatar={<Avatar size={20} url={user?.avatar_url} label={name} />} {...props}>
      {name} {count}
    </Chip>
  )
}

const ReviewFilter = ({ filter, count, ...props }: FilterProps<CollabItemsReviewFilter>) => {
  return (
    <Chip size="sm" icon={Review} {...props}>
      Reviews {count}
    </Chip>
  )
}

const ResolvedFilter = observer(({ filter, ...props }: FilterProps<CollabItemsResolvedFilter>) => {
  /**
   * The resolved filter always shows the total count of resolved items, so we override the prop here.
   */
  const docStore = useDocStore()
  const count = computed(() => docStore.collabItems.filter((item) => filter.fn(item)).length).get()

  return (
    <Chip size="sm" icon={Check} {...props}>
      Resolved {count}
    </Chip>
  )
})

export const AllFilter = observer(() => {
  const docStore = useDocStore()
  const count = computed(() => docStore.collabItems.filter((item) => !resolvedFilter.fn(item)).length).get()
  const isSelected = computed(() => docStore.filters.activeKeys.size === 0).get()

  if (count === 0) {
    return null
  }

  const handleChange = (selected: boolean) => {
    if (selected) {
      docStore.filters.clear()
    }
  }

  return (
    <Chip size="sm" deletable={false} onChange={handleChange} isSelected={isSelected}>
      All {count}{' '}
    </Chip>
  )
})

const CollabItemsFilter = observer(({ filter }: CollabItemsFilterProps) => {
  const docStore = useDocStore()
  const isSelected = computed(() => docStore.filters.activeKeys.has(filter.key)).get()
  const count = docStore.filters.count(filter)

  useEffect(() => {
    if (count === 0) {
      docStore.filters.deactivate(filter.key)
    }
  }, [count, docStore.filters, filter.key])

  if (count === 0) {
    return null
  }

  const props = {
    isSelected,
    count,
    onChange: () => (isSelected ? docStore.filters.deactivate(filter.key) : docStore.filters.activate(filter.key)),
  }

  switch (filter.facet) {
    case 'itemType':
      switch (filter.key) {
        case 'comments':
          return <CommentsFilter filter={filter} {...props} />
        case 'trackedChanges':
          return <TrackedChangesFilter filter={filter} {...props} />
        case 'todos':
          return <TodosFilter filter={filter} {...props} />
        default:
          return null
      }

    case 'user':
      return <UserFilter filter={filter} {...props} />

    case 'review':
      return <ReviewFilter filter={filter} {...props} />

    case 'resolved':
      return <ResolvedFilter filter={filter} {...props} />
  }
})

export { CollabItemsFilter }
